import { Component, computed, effect, inject, OnInit, signal } from '@angular/core';
import { Router, NavigationEnd, ActivatedRoute } from '@angular/router';
import { filter, map, take } from 'rxjs/operators';
import { AuthService } from '@shared/services/auth.service';
import { SidebarService } from '@shared/services/sidebar.service';
import { SpinnerService } from '@shared/services';
import { PrivacyPolicyService } from '@shared/services/privacy-policy.service';
import { BillingStateService } from '@shared/services/billing-state.service';
import { AffiliateService } from '@shared/services/affiliate.service';
import { PartnerEmbedService } from '@shared/services/partner-embed.service';
import { FunnelService } from '@shared/services/funnel.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss'
})
export class AppComponent implements OnInit {
  public readonly sidebarService = inject(SidebarService);
  public readonly authService = inject(AuthService);
  public readonly spinnerService = inject(SpinnerService);
  public readonly partnerEmbed = inject(PartnerEmbedService);
  public readonly funnel = inject(FunnelService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly privacyPolicyService = inject(PrivacyPolicyService);
  private readonly billingState = inject(BillingStateService);
  private readonly affiliateService = inject(AffiliateService);

  // Track if current route should hide the layout (defined in route data: { hideLayout: true })
  public readonly hideLayout = signal(false);

  // Affiliate banner follows the currently active affiliate (AffiliateService),
  // so switching the code in the registration form updates it immediately.
  private readonly affiliateBannerDismissed = signal(this.affiliateService.isBannerDismissed());
  // Display name of the referring affiliate (null = no banner)
  public readonly affiliateName = computed(() =>
    this.affiliateBannerDismissed() ? null : (this.affiliateService.active()?.name ?? null));
  // Per-affiliate banner text from `affiliates.discount_label` (null = generic 10 % text)
  public readonly affiliateLabel = computed(() => this.affiliateService.active()?.label || null);

  constructor() {
    // Track route changes to detect hideLayout from route data
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      map(() => this.getHideLayoutFromRoute())
    ).subscribe(hideLayout => {
      this.hideLayout.set(hideLayout);
    });

    // React to authentication changes and check privacy policy
    effect(async () => {
      const isAuthenticated = this.authService.isAuthenticated();

      if (isAuthenticated) {
        const currentUrl = this.router.url || '';

        // Gast-Sessions (Issue #384): kein Billing, keine globalen Modals — der
        // Editor übernimmt die Account-Übernahme bei der Abgabe.
        if (this.funnel.isGuest()) {
          return;
        }
        // Echter Account auf diesem Gerät bekannt → "/" führt künftig zum Login statt zu /start
        this.funnel.markKnownUser();

        // During auth/onboarding routes we don't show global modals.
        // Profile completion flow is handled inside the auth/register wizard (checkboxes + required fields).
        if (currentUrl.startsWith('/auth/')) {
          // We're still on an auth route — defer billing load until navigation completes
          this.router.events.pipe(
            filter((event): event is NavigationEnd => event instanceof NavigationEnd),
            filter(event => !event.urlAfterRedirects.startsWith('/auth/')),
            take(1)
          ).subscribe(() => {
            this.billingState.load();
          });
          return;
        }

        // Students and Graders are managed differently — skip billing + global privacy modal
        // But still load billing for org members (for org badge / org license credits)
        if (this.authService.isStudent() || this.authService.isGrader()) {
          this.billingState.load();
          return;
        }

        // Load billing before profile-completion check so that the org badge and
        // credit display work even for users who haven't finished onboarding
        // (e.g. kanzlei_admin accounts created manually without going through the wizard)
        this.billingState.load();

        // If the user still needs to complete their profile, don't show privacy modal yet.
        if (this.authService.needsProfileCompletion()) {
          return;
        }

        // Regular users: check privacy policy
        await this.privacyPolicyService.init();
        await this.privacyPolicyService.showPrivacyUpdateModalIfNeeded();
      }
    }, { allowSignalWrites: true });
  }

  ngOnInit() {
    // Check initial route for hideLayout data
    this.hideLayout.set(this.getHideLayoutFromRoute());

    // First-Touch-Attribution (Issue #384): ?ref= (Affiliate), ?c=/utm_campaign
    // (Kampagne), utm_source/medium/content aus der Landing-URL merken, damit
    // Registrierung bzw. Gast-Übernahme den Nutzer der Quelle zuordnen können.
    // Schreibt auch die Legacy-Keys kk_ref / kk_campaign (AffiliateService,
    // RegisterService). First touch wins.
    this.funnel.captureAttribution();

    // Show the discount banner for visitors who came via an affiliate link,
    // until they register (banner is only rendered while unauthenticated).
    this.loadAffiliateBanner();

    // Refresh auth to ensure we have the latest user data
    this.authService.refreshAuth();
  }

  private async loadAffiliateBanner(): Promise<void> {
    const slug = this.affiliateService.getStoredRef();
    if (!slug) return;
    const info = await this.affiliateService.getInfo(slug);
    // Don't overwrite a code the user validated in the meantime (register form).
    if (!this.affiliateService.active()) this.affiliateService.setActive(slug, info);
  }

  dismissAffiliateBanner(): void {
    this.affiliateService.dismissBanner();
    this.affiliateBannerDismissed.set(true);
  }

  /**
   * Walk up the route tree to find hideLayout data property
   */
  private getHideLayoutFromRoute(): boolean {
    let currentRoute = this.route.root;
    while (currentRoute.firstChild) {
      currentRoute = currentRoute.firstChild;
      if (currentRoute.snapshot.data['hideLayout']) {
        return true;
      }
    }
    return false;
  }
}
